<template>
    <component
        :is="navigationComponent"
        :entries="getNavigation"
        :settings="settings"
        :home-uri="homeUri"
        :show-search="!isMicrosite"
        :google-translate-settings="googleTranslateSettings"
        :logo-hidden="!displayHeaderLogo"
        :data-mode="navColorMode"
        :data-theme="navColorTheme"
    >
        <template #logo>
            <img
                v-if="logo"
                :src="logo.url"
                :alt="`${siteDetails?.name} logo`"
                :class="logo.size"
                class="object-contain"
            >
        </template>
    </component>
</template>

<script setup>
    const {
        homeUri,
        getNavigation,
        globalSets,
        digistormSettings,
        getGlobalSetField,
        siteDetails,
        pageType,
        hasScrolled,
    } = storeToRefs(useGlobalStore())

    const { entry } = storeToRefs(useEntryStore())
    const { isMicrosite } = storeToRefs(useMicrositeStore())

    const navigationType = computed(() => {
        return digistormSettings.value.navType
    })

    const navigationTypes = import.meta.glob('./nav-types/*.async.vue')

    const settings = computed(() => {
        const navigationSet = find(globalSets.value, { __typename: 'ds_Navigation_GlobalSet' })

        if (!navigationSet) {
            return {}
        }

        const settings = {
            ...Object.entries(navigationSet).reduce((acc, [key]) => {
                acc[key] = getGlobalSetField.value('ds_Navigation_GlobalSet', key)
                return acc
            }, {}),
        }

        const primaryNavSettings = first(settings.primaryNav) || null

        // remove primaryNav array from settings
        delete settings.primaryNav

        return {
            ...primaryNavSettings,
            ...settings,
        }
    })

    const googleTranslateSettings = computed(() => {
        // Find the ds_GoogleTranslate_GlobalSet in globalSets
        const googleTranslateSet = find(globalSets.value, { __typename: 'ds_GoogleTranslate_GlobalSet' })

        if (!googleTranslateSet?.googleTranslate_OptionsValue) {
            return {
                toggle: false,
                options: [],
            }
        }

        // Combine googleTranslate_Options and googleTranslate_OptionsValue
        const options = googleTranslateSet.googleTranslate_OptionsValue.map((code, index) => ({
            code,
            name: googleTranslateSet.googleTranslate_Options[index],
        }))

        return {
            toggle: googleTranslateSet.googleTranslate_Toggle,
            options,
        }
    })

    const navigationComponent = defineAsyncComponent(() => {
        const navigation = navigationTypes[`./nav-types/${navigationType.value}.async.vue`]
        return navigation ? navigation() : Promise.reject(new Error(`Unknown navigation type: ${navigationType.value}`))
    })

    const showBackground = computed(() => {
        return pageType.value === 'home'
            ? settings.value?.primaryNav_ShowBackground
            : settings.value?.primaryNav_InternalPageShowBackground
    })

    const navColorMode = computed(() => {
        const { colorPalette } = useColorPalette()

        const bannerHasMedia = Boolean(
            get(entry.value, 'banner.0.bannerImage.0.url') ||
            get(entry.value, 'banner.0.bannerVideo.0.videoUrl') ||
            get(entry.value, 'banner.0.bannerVideo.0.videoFile.0.url'),
        )

        // if the banner has media & showBackground is false, we want to force dark mode
        if (bannerHasMedia && !showBackground.value) {
            return 'brand-invert'
        }

        const navPalette =
            pageType.value === 'home'
                ? settings.value?.primaryNav_ColorPalette
                : settings.value?.primaryNav_InternalPageColorPalette

        if (hasScrolled.value) {
            return colorPalette(settings.value?.primaryNav_StickyNavColorPalette || navPalette)
        }
        return colorPalette(navPalette)
    })

    const navColorTheme = computed(() => {
        return pageType.value === 'home'
            ? settings.value?.primaryNav_ColorTheme
            : settings.value?.primaryNav_InternalPageColorTheme
    })

    const logo = computed(() => {
        if (!displayHeaderLogo.value) {
            return null
        }

        const stickLogoType = getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_StickyNavLogoType', false)

        // if we have scrolled
        if (hasScrolled.value) {
            // if stickLogotype is custom, use the sticky logo & it's size field
            if (stickLogoType === 'custom') {
                const stickyLogo = getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_StickyNavLogo', true)
                const stickyLogoUrl = stickyLogo?.[0]?.url

                if (stickyLogoUrl) {
                    return {
                        url: stickyLogoUrl,
                        size: logoScale(getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_StickyNavLogoSize', true)),
                    }
                }
            } else if (stickLogoType === 'none') {
                return null
            }
        }

        /*
         ** If we reach here, we either have a stickyNavType that set to 'header-logo'
         ** or we haven't scrolled, in both cases we want to show the header logo
         */

        // if the nav is in 'brand-invert' mode, we want to use the header logo dark variant
        const logo =
            navColorMode.value === 'brand-invert'
                ? getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_HeaderLogoDark', true)
                : getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_HeaderLogo', true)

        return {
            url: logo?.[0]?.url,
            size: logoScale(getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_HeaderLogoSize', true)),
        }
    })

    const logoPosition = computed(() => {
        return settings.value?.primaryNav_LogoPosition || 'left'
    })

    const bannerType = computed(() => {
        return digistormSettings.value.bannerType
    })

    const displayHeaderLogo = computed(() => {
        // if not on home page or we've scrolled, we always display a logo
        if (pageType.value !== 'home' || hasScrolled.value) {
            return true
        }

        const bannerLogos = {
            'banner-a': { typename: 'ds_BannerA_Entry', logoPath: 'bannerA_Logo.0.url' },
            'banner-b': { typename: 'ds_BannerB_Entry', logoPath: 'bannerB_Logo.0.url' },
            'banner-e': { typename: 'ds_BannerE_Entry', logoPath: 'bannerE_Logo.0.url' },
            'banner-custom': { typename: 'custom_Banner_Entry', logoPath: 'customBanner_Logo.0.url' },
        }

        const bannerHasLogo = bannerLogos[bannerType.value]
        if (bannerHasLogo) {
            const banner = first(entry.value?.banner?.filter((b) => b.__typename === bannerHasLogo.typename))
            return !get(banner, bannerHasLogo.logoPath, null)
        }

        return true
    })

    const logoScale = (size) => {
        switch (size) {
            case 'sm':
                return ['scale-50', logoPosition.value === 'left' && 'translate-x-[-25%]']
            case 'lg':
                return ['scale-100 md:scale-150', logoPosition.value === 'left' && 'md:translate-x-[25%]']
            case 'md':
            default:
                return [
                    'scale-75 md:scale-100',
                    logoPosition.value === 'left' && 'translate-x-[-12.5%] md:translate-x-0',
                ]
        }
    }

    const handleScroll = () => {
        if (!hasScrolled.value && window.scrollY > 100) {
            hasScrolled.value = true
        } else if (hasScrolled.value && window.scrollY < 50) {
            // Buffer to prevent flickering when scrolling near the threshold
            hasScrolled.value = false
        }
    }

    onMounted(() => {
        window.addEventListener('scroll', handleScroll)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', handleScroll)
    })
</script>
